import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import baseUrl from '../api/utils';

const fmt = (n) => (n || n === 0) ? Number(n).toLocaleString("en-IN") : "—";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr; // already formatted / not a date
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// ═══════════════════════════════════════════════════════════════════════════════
// VIEW ADMIN PROFILE — reached from ViewAdmin.jsx's row click
// (Link to={`/viewAdmin/profile/${user.email}`} state={{ user }}).
// Shows the admin's basic details (from router state) plus their company's
// live license status (SaaS or VPS), fetched fresh from the backend.
// ═══════════════════════════════════════════════════════════════════════════════
const ViewAdminProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = sessionStorage.getItem("token");

  // admin basic info passed via router state from ViewAdmin.jsx
  const user = location.state?.user || {};

  const [license, setLicense] = useState(null);
  const [licenseType, setLicenseType] = useState(null); // "SaaS" | "VPS" | null (not found)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLicense = async () => {
      if (!user.companyName) {
        setLoading(false);
        setError("No company name available for this admin.");
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(`${baseUrl}/api/v2/license/status-by-company`, {
          headers: { Authorization: token },
          params: { company: user.companyName },
        });

        if (res.status === 200 && res.data) {
          // SaaS responses use LicenseDto (has "exists"); VPS responses use CompanyLicenseDto (has "lastCheckIn")
          if (res.data.lastCheckIn !== undefined) {
            setLicense(res.data);
            setLicenseType("VPS");
          } else if (res.data.exists) {
            setLicense(res.data);
            setLicenseType("SaaS");
          } else {
            setLicense(null);
            setLicenseType(null);
          }
        } else {
          setLicense(null);
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          navigate("/unauthorized");
        } else {
          console.error("Error fetching license for company:", err);
          setError("Could not load license details for this company.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchLicense();
  }, [user.companyName]);

  // SaaS uses LicenseDto.valid (boolean, already computed server-side).
  // VPS uses CompanyLicenseDto.licenseStatus ("ACTIVE" | "EXPIRED").
  const isExpired = () => {
    if (!license) return null;
    if (licenseType === "SaaS") return license.valid === false;
    if (license.licenseStatus) return license.licenseStatus === "EXPIRED";
    return null;
  };

  const expiredFlag = isExpired();

  return (
    <div>
      <div className="page-header"></div>
      <div className='row'>
        <div className='col-sm-12'>

          {/* ── ADMIN DETAILS CARD ── */}
          <div className='card mb-3'>
            <div className='card-header'>
              <div className="tableheader2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h4 className="mb-0">Admin Profile</h4>
                <button className="btn btn-light" onClick={() => navigate(-1)}>
                  <i className="fa-solid fa-arrow-left mr-1"></i> Back
                </button>
              </div>
            </div>
            <div className='card-body'>
              <div className="row">
                <div className="col-md-3">
                  <label className='text-muted mb-1' style={{ fontSize: 12 }}>Username</label>
                  <p className='font-weight-bold'>{user.username || "—"}</p>
                </div>
                <div className="col-md-3">
                  <label className='text-muted mb-1' style={{ fontSize: 12 }}>Email</label>
                  <p className='font-weight-bold'>{user.email || "—"}</p>
                </div>
                <div className="col-md-3">
                  <label className='text-muted mb-1' style={{ fontSize: 12 }}>Company Name</label>
                  <p className='font-weight-bold'>{user.companyName || "—"}</p>
                </div>
                <div className="col-md-3">
                  <label className='text-muted mb-1' style={{ fontSize: 12 }}>Phone</label>
                  <p className='font-weight-bold'>{user.phone || "—"}</p>
                </div>
                <div className="col-md-3">
                  <label className='text-muted mb-1' style={{ fontSize: 12 }}>GSTIN</label>
                  <p className='font-weight-bold'>{user.gstinNumber || "—"}</p>
                </div>
                <div className="col-md-3">
                  <label className='text-muted mb-1' style={{ fontSize: 12 }}>Date of Birth</label>
                  <p className='font-weight-bold'>{user.dob || "—"}</p>
                </div>
                <div className="col-md-3">
                  <label className='text-muted mb-1' style={{ fontSize: 12 }}>Status</label>
                  <p>
                    {user.isActive === true ? (
                      <span className='Activeuser'><i className="fa-solid fa-circle pr-2"></i>Active</span>
                    ) : (
                      <span className='InActiveuser'><i className="fa-solid fa-circle pr-2"></i>In Active</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── LICENSE DETAILS CARD ── */}
          <div className='card'>
            <div className='card-header'>
              <div className="tableheader2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h4 className="mb-0">License Details</h4>
                {licenseType && (
                  <span
                    className={`badge ${licenseType === "VPS" ? "badge-info" : "badge-success"}`}
                    style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: '20px', letterSpacing: '0.5px' }}
                  >
                    {licenseType}
                  </span>
                )}
              </div>
            </div>
            <div className='card-body'>
              {loading ? (
                <div className='text-muted'>Loading license details…</div>
              ) : error ? (
                <div className='text-danger'>{error}</div>
              ) : !license ? (
                <div className='text-muted'>No license record found for this company.</div>
              ) : (
                <>
                  {expiredFlag !== null && (
                    <div className={`alert ${expiredFlag ? "alert-danger" : "alert-success"} py-2 px-3`} style={{ display: 'inline-block', fontSize: 13, marginBottom: 18 }}>
                      <i className={`fa-solid ${expiredFlag ? "fa-triangle-exclamation" : "fa-circle-check"} mr-2`}></i>
                      {expiredFlag ? "This license is EXPIRED" : "This license is ACTIVE"}
                      {licenseType === "SaaS" && license.daysRemaining !== undefined && license.daysRemaining !== null && (
                        <span> — {license.daysRemaining >= 0
                          ? `${license.daysRemaining} day(s) remaining`
                          : `expired ${Math.abs(license.daysRemaining)} day(s) ago`}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="row">
                    {license.licenseType && (
                      <div className="col-md-3">
                        <label className='text-muted mb-1' style={{ fontSize: 12 }}>License Type</label>
                        <p className='font-weight-bold'>{license.licenseType}</p>
                      </div>
                    )}
                    {(license.maxUsers !== undefined) && (
                      <div className="col-md-3">
                        <label className='text-muted mb-1' style={{ fontSize: 12 }}>Max Users</label>
                        <p className='font-weight-bold'>{fmt(license.maxUsers)}</p>
                      </div>
                    )}
                    {(license.usersUsed !== undefined) && (
                      <div className="col-md-3">
                        <label className='text-muted mb-1' style={{ fontSize: 12 }}>Users Used</label>
                        <p className='font-weight-bold'>{fmt(license.usersUsed)}</p>
                      </div>
                    )}
                    {license.modulesEnabled && (
                      <div className="col-md-3">
                        <label className='text-muted mb-1' style={{ fontSize: 12 }}>Modules Enabled</label>
                        <p className='font-weight-bold'>{license.modulesEnabled}</p>
                      </div>
                    )}
                    {license.productname && (
                      <div className="col-md-3">
                        <label className='text-muted mb-1' style={{ fontSize: 12 }}>Product</label>
                        <p className='font-weight-bold'>{license.productname}</p>
                      </div>
                    )}
                    {(license.startDate) && (
                      <div className="col-md-3">
                        <label className='text-muted mb-1' style={{ fontSize: 12 }}>Start Date</label>
                        <p className='font-weight-bold'>{formatDate(license.startDate)}</p>
                      </div>
                    )}
                    {(license.endDate || license.expiryDate) && (
                      <div className="col-md-3">
                        <label className='text-muted mb-1' style={{ fontSize: 12 }}>Valid Till</label>
                        <p className='font-weight-bold'>{formatDate(license.endDate || license.expiryDate)}</p>
                      </div>
                    )}
                    {license.lastCheckIn && (
                      <div className="col-md-3">
                        <label className='text-muted mb-1' style={{ fontSize: 12 }}>Last Check-In</label>
                        <p className='font-weight-bold'>{formatDate(license.lastCheckIn)}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ViewAdminProfile;